import {
  MatchAccessibility,
  MatchPlayerRole,
  MatchPlayerStatus,
  MatchStatus,
} from '@okletsplay/core/src/modules/match/enums';
import { ModalName } from 'app/enums';
import { MatchEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import { MatchAccordionDescription } from 'modules/matches/components/MatchAccordionDescription/MatchAccordionDescription';
import { JoinRequests } from 'modules/matches/components/MatchModal/JoinRequests';
import { MatchModalHeader } from 'modules/matches/components/MatchModal/MatchModalHeader';
import { OwnerActions } from 'modules/matches/components/MatchModal/OwnerActions';
import { MatchModalPlayerActions } from 'modules/matches/components/MatchModal/MatchModalPlayerActions';
import { MatchPlayers } from 'modules/matches/components/MatchModal/MatchPlayers';
import { Withdraw } from 'modules/matches/components/MatchModal/Withdraw';
import { MatchSummary } from 'modules/matches/components/MatchSummary';
import { MatchPanelType } from 'modules/matches/enums';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Accordion } from 'shared/components/Accordion';
import { Modal, ModalProps } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { MATCH_PLAYERS_DEFAULT_SETTINGS } from 'modules/matches/constants';

type MatchModalProps = Partial<ModalProps> & {
  match: MatchEntity;
};

const MatchModalComponent: FC<MatchModalProps> = ({ match, onClose }) => {
  const [expanded, setExpanded] = useState<MatchPanelType>(MatchPanelType.MatchSummary);

  const currentUser = useSelector(sessionSelector.activeUser);

  const currentPlayer = useMemo(
    () => match?.players?.find((player) => player?.user?.id === currentUser?.id),
    [currentUser?.id, match?.players]
  );
  const requestedPlayers = useMemo(
    () => match?.players?.filter((player) => player.status === MatchPlayerStatus.Requested),
    [match?.players]
  );

  const isAuthor = useMemo(() => currentPlayer?.role === MatchPlayerRole.Owner, [currentPlayer]);
  const isPrivateMatch = useMemo(
    () => match?.accessibility === MatchAccessibility.Private,
    [match?.accessibility]
  );
  const isDeclinedMatch = useMemo(() => match?.status === MatchStatus.Declined, [match?.status]);
  const matchDescription = useMemo(
    () => (isPrivateMatch ? 'Private Match' : 'Open Match'),
    [isPrivateMatch]
  );
  const isWithdrawAvailable = useMemo(
    () => !isDeclinedMatch && (isAuthor || currentPlayer?.status === MatchPlayerStatus.Requested),
    [isDeclinedMatch, isAuthor, currentPlayer?.status]
  );
  const isJoinApproveRequired = useMemo(
    () => match?.isJoinApproveRequired,
    [match?.isJoinApproveRequired]
  );
  const isJoinRequestsAvailable = useMemo(
    () => !isPrivateMatch && isJoinApproveRequired && isAuthor && !isDeclinedMatch,
    [isPrivateMatch, isJoinApproveRequired, isAuthor, isDeclinedMatch]
  );

  const handleChange = useCallback((panel: MatchPanelType) => setExpanded(panel), [setExpanded]);

  if (!match) {
    return null;
  }

  return (
    <Modal
      maxWidth="md"
      contentHeight={630}
      title={isPrivateMatch ? 'Private Match' : 'Open Match'}
      name={ModalName.Match}
      onClose={onClose}
      disableEnforceFocus={true}
      header={
        <MatchModalHeader title={matchDescription} matchId={match.id} createdAt={match.createdAt} />
      }
      actions={
        isAuthor ? (
          <OwnerActions
            matchId={match.id}
            openMatchPanel={handleChange}
            accessibility={match.accessibility}
            matchStatus={match.status}
            players={match.players}
            platformType={match.game?.platformType}
            isJoinApproveRequired={isJoinApproveRequired}
          />
        ) : (
          <MatchModalPlayerActions match={match} playerStatus={currentPlayer?.status} />
        )
      }
    >
      <Accordion
        title="Match Summary"
        onChange={() => handleChange(MatchPanelType.MatchSummary)}
        expanded={expanded === MatchPanelType.MatchSummary}
      >
        <MatchSummary
          game={match.game}
          entryFee={match.entryFee}
          accessibility={match.accessibility}
          clockLimit={match.clockLimit}
          clockIncrement={match.clockIncrement}
          players={match.players}
          maxPlayersCount={
            match.playersSettings?.maxPlayersCount ?? MATCH_PLAYERS_DEFAULT_SETTINGS.maxPlayersCount
          }
          matchSetupSettings={match.matchSetupSettings}
        />
      </Accordion>
      {isJoinRequestsAvailable && (
        <Accordion
          title="Join Requests"
          description={
            <MatchAccordionDescription
              text={requestedPlayers?.length ? `${requestedPlayers?.length} PENDING REQUESTS` : ''}
            />
          }
          onChange={() => handleChange(MatchPanelType.JoinRequests)}
          expanded={expanded === MatchPanelType.JoinRequests}
        >
          <JoinRequests players={requestedPlayers} matchId={match?.id} gameId={match?.game?.id} />
        </Accordion>
      )}
      <Accordion
        title="Players"
        onChange={() => handleChange(MatchPanelType.TeamSelection)}
        description={<MatchAccordionDescription text={matchDescription} />}
        expanded={expanded === MatchPanelType.TeamSelection}
      >
        <MatchPlayers players={match.players} matchStatus={match?.status} />
      </Accordion>
      {isWithdrawAvailable && (
        <Accordion
          title="Withdraw"
          onChange={() => handleChange(MatchPanelType.Withdraw)}
          expanded={expanded === MatchPanelType.Withdraw}
          dataTestId="withdraw-accordion"
        >
          <Withdraw isAuthor={isAuthor} matchId={match?.id} />
        </Accordion>
      )}
    </Modal>
  );
};

export const MatchModal = withModalOpenChecker(MatchModalComponent, ModalName.Match);
